import { useEffect, useState, useCallback, useMemo } from "react";
import { Calculator, Archive as ArchiveIcon, Search, X as XIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import KanbanBoard from "../components/KanbanBoard";
import AccountCardDrawer from "../components/AccountCardDrawer";
import api from "../lib/api";
import { toast } from "sonner";

export default function AccountingBoard({ archived = false }) {
  const [columns, setColumns] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const [colRes, cardRes] = await Promise.all([
        api.get("/board-columns/accounting"),
        api.get(`/accounting/cards?archived=${archived}`),
      ]);
      setColumns(colRes.data);
      setCards(cardRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load board");
    } finally { setLoading(false); }
  }, [archived]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (cardId, newStatusName) => {
    await api.put(`/accounting/cards/${cardId}/status`, { status: newStatusName });
    await load();
    window.dispatchEvent(new CustomEvent("accounting-changed"));
  };

  const activeColumns = archived
    ? columns.filter(c => c.name === "Captured")
    : columns;

  // Client-side filter so users can narrow to a single JC across all
  // columns. Matches against JC number, model, store and area —
  // sufficient for the day-to-day "where's that one card?" lookup.
  const filteredCards = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => {
      return (
        (c.job_card_number || "").toLowerCase().includes(q) ||
        (c.model || "").toLowerCase().includes(q) ||
        (c.store || "").toLowerCase().includes(q) ||
        (c.area || "").toLowerCase().includes(q)
      );
    });
  }, [cards, filter]);

  return (
    <div className="p-4 lg:p-6 max-w-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {archived ? (
            <>
              <ArchiveIcon size={22} className="text-teal-600" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-slate-800" data-testid="page-title">Archive</h1>
                <p className="text-xs text-slate-500">Captured records</p>
              </div>
            </>
          ) : (
            <>
              <Calculator size={22} className="text-teal-600" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-slate-800" data-testid="page-title">Completed JC</h1>
                <p className="text-xs text-slate-500">Drag cards between columns or click a card to review</p>
              </div>
            </>
          )}
        </div>
        <Button
          data-testid="refresh-board-btn"
          variant="outline"
          size="sm"
          onClick={load}
          className="text-xs"
        >
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-12 text-center">Loading board…</div>
      ) : (
        <>
          {/* Filter row — narrows visible cards by JC#, model, store, area */}
          <div className="mb-3 flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="accounting-filter-input"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by JC#, model, store or area…"
                className="pl-8 h-9 text-sm bg-white"
              />
              {filter && (
                <button
                  type="button"
                  data-testid="accounting-filter-clear"
                  onClick={() => setFilter("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                >
                  <XIcon size={14} />
                </button>
              )}
            </div>
            <span className="text-xs text-slate-400" data-testid="accounting-filter-count">
              {filter ? `${filteredCards.length} of ${cards.length}` : `${cards.length} card(s)`}
            </span>
          </div>
          <KanbanBoard
            boardKey="accounting"
            columns={activeColumns}
            setColumns={setColumns}
            cards={filteredCards}
            statusField="accounting_status"
            onStatusChange={handleStatusChange}
            onCardClick={(c) => setSelectedCardId(c.id)}
            canAddColumn={!archived}
            emptyHint={archived ? "No archived records yet." : "No completed job cards yet."}
          />
        </>
      )}

      {selectedCardId && (
        <AccountCardDrawer
          cardId={selectedCardId}
          boardKey="accounting"
          currentRole="SA"
          availableStatuses={columns.map(c => c.name)}
          onClose={() => setSelectedCardId(null)}
          onUpdated={load}
        />
      )}
    </div>
  );
}
